import {
  getElementComponent,
  getElementFromStateById,
  getParentElement,
  createElementFromCopy,
  transformElementValue,
} from '@ey-content-parser/core/es';

function TableBuildHead(tableId, data, parserFn) {
  const tableHead = parserFn(data, [], false).reduce(
    (acc, next) => {
      next.parentId = tableId;
      acc.tableHeadElements[next.id] = next;
      acc.tableHeadOrder.push(next.id);
      return acc;
    },
    { tableHeadElements: {}, tableHeadOrder: [] },
  );
  return tableHead;
}

// we may remove this watch function
// since the header column will always be present
function TableWatchShowLogic(getUpdateColumnFn) {
  return colIndex => newState => {
    //const updateFn = getUpdateColumnFn(colIndex);
    //updateFn('visible', newState.isComponentVisible);
  };
}

function TableGetChildren(tableHeadElements, elementActions) {
  const { getParserState } = elementActions();
  const { elements } = getParserState();

  let tableElements = [];
  let otherElements = [];

  elements.forEach(el => {
    if (tableHeadElements[el.logicId] && tableHeadElements[el.logicId].parentId === el.parentId) {
      tableElements.push(el);
    } else {
      otherElements.push(el);
    }
  });

  return { tableElements, otherElements };
}

function TableRebuildModel(newRows, otherElements, tableHeadElements, elementActions, tableActions) {
  const { setParserState, getParserState, refreshShowLogic } = elementActions();

  // rebuild the table rows
  const { rowsElements, rowsValues } = TableBuildRows(newRows, tableHeadElements, elementActions, tableActions);
  const newColumns = TableBuildColumnOptions(tableHeadElements, elementActions, tableActions);

  // refresh the parser state to reflect the newly added row in the first row position
  setParserState(otherElements.concat(rowsElements));

  // update the parent component props
  const { elements, elementsIndex } = getParserState();
  const colName = Object.keys(tableHeadElements)[0];
  const colEl = tableHeadElements[colName];

  const parentNode = getParentElement(colEl, elements, elementsIndex);
  const parentUpdateFn = parentNode.updateComponent;

  // refresh the table to reflect the new rows
  parentUpdateFn({ columns: newColumns, rows: rowsValues });

  // refresh showLogic on newly rebuilt model
  refreshShowLogic();

  //refresh the table head to show hidden columns
  const updatedColumns = refreshColumnsOptions(getParserState().elements, newColumns, rowsValues);
  parentUpdateFn({ columns: updatedColumns, rows: rowsValues });
}

function refreshColumnsOptions(elements, newColumns, rowsValues) {
  let updatedColumns = [];

  // if there are no elements it's an empty table
  // in that case we return the default columns
  if (elements && elements.length === 0) {
    return newColumns;
  }
  // when there are no rows we need to show the default columns
  if (rowsValues && rowsValues.length === 0) {
    return newColumns;
  }
  // go through each element and search by `logicId` and determine if there is one visible element.
  // if there is a visible element then we are mark the table column `visible` to `true`

  newColumns.forEach(currentColumn => {
    let currentElements = elements.filter(elm => {
      return elm.logicId === currentColumn.id;
    });

    if (currentElements.length) {
      let visibleElements = currentElements.filter(obj => {
        return obj.props.isComponentVisible === true;
      });

      currentColumn.visible = visibleElements.length > 0;
      if (visibleElements && visibleElements.length > 0) {
        visibleElements.forEach(value => {
          if (value.props.controlTypeName === 'Caption') {
            currentColumn.visible = false;
          }
        });
      }
      updatedColumns.push(currentColumn);
    }
  });

  return updatedColumns;
}

function TableAddRow(defaultValues, tableHeadElements, tableHeadOrder, elementActions, tableActions) {
  const { tableElements, otherElements } = TableGetChildren(tableHeadElements, elementActions);
  const { getParserState, setFormValue } = elementActions();

  const totalRows = tableElements.length / tableHeadOrder.length;
  let counter = 0;

  const { setRowId } = tableActions;
  const origRowIndex = setRowId();

  let newRow = [];

  /* initialize formValues with new row and default values */
  const { elements, elementsIndex } = getParserState();

  tableHeadOrder.forEach((col, colIndex) => {
    const defaultValue = defaultValues && defaultValues[colIndex] ? defaultValues[colIndex] : '';

    newRow.push({
      elementName: col,
      value: (defaultValues && defaultValues[colIndex]) || '',
      origRowIndex,
    });

    const { logicId, parentId } = tableHeadElements[col];

    const formValue = transformElementValue({ ...tableHeadElements[col], origRowIndex }, defaultValue, { elements, elementsIndex }, false);
    setFormValue(logicId, formValue, parentId, origRowIndex, colIndex);
  });

  const newRows = Array.from({ length: totalRows }).reduce(
    (acc, next, index) => {
      const colData = tableHeadOrder.map((col, colIndex) => ({
        elementName: tableElements[counter].logicId,
        origRowIndex: tableElements[counter].origRowIndex,
        value: tableElements[counter++].props.value,
      }));

      acc[index + 1] = colData;
      return acc;
    },
    { 0: newRow },
  );

  TableRebuildModel(newRows, otherElements, tableHeadElements, elementActions, tableActions);
}

function TableDeleteRows(rows, tableHeadElements, tableHeadOrder, elementActions, tableActions) {
  const { tableElements, otherElements } = TableGetChildren(tableHeadElements, elementActions);

  const rowsToRemove = rows.map(row => row.origRowIndex);
  const totalRows = tableElements.length / tableHeadOrder.length;

  let elementCounter = 0;
  let rowCounter = 0;

  const newRows = Array.from({ length: totalRows }).reduce((acc, next, index) => {
    const colData = tableHeadOrder.map((col, colIndex) => ({
      elementName: tableElements[elementCounter].logicId,
      origRowIndex: tableElements[elementCounter].origRowIndex,
      value: tableElements[elementCounter++].props.value,
    }));

    if (rowsToRemove.includes(index)) {
      const { getParserState, setFormValue } = elementActions();
      const { elements, elementsIndex } = getParserState();

      const elStartPosition = elementCounter - tableHeadOrder.length;

      // update the isDeleted flag in formValues
      tableHeadOrder.forEach((col, colIndex) => {
        const targetEl = tableElements[elStartPosition + colIndex];
        const formValue = transformElementValue(targetEl, targetEl.defaultValue, { elements, elementsIndex }, true);
        setFormValue(
          targetEl.logicId,
          {
            ...formValue,
            value: '',
            AnswerPageID: targetEl.answerPageId,
            SourceTableId: targetEl.sourceTableId,
            isDeleted: true,
          },
          targetEl.parentId,
          targetEl.origRowIndex,
          colIndex,
        );
      });

      return acc;
    }

    acc[rowCounter++] = colData;
    return acc;
  }, {});

  TableRebuildModel(newRows, otherElements, tableHeadElements, elementActions, tableActions);
}

function TableBuildColumnOptions(tableHeadElements, elementActions, tableActions) {
  let tableHeadColumnOptions = [];

  Object.entries(tableHeadElements).forEach(([_, col], index) => {
    tableHeadColumnOptions.push({
      id: col.id,
      displayText: col.label,
      sortable: true,
      visible: col.props.isComponentVisible,
      renderComponent: (value, meta, updateModelFn) => {
        return getElementFromStateById(col.id, meta.origRowIndex, elementActions);
      },
      customSort: col.customSort,
    });
  });

  return tableHeadColumnOptions;
}

function TableBuildForm(tableHeadElements, elementActions, rowIndex = '', defaultValues = {}) {
  const { getParserState, setParserState, refreshShowLogic } = elementActions();

  let popupElements = [];
  let popupChildren = [];

  Object.entries(tableHeadElements).forEach(([key, element], index) => {
    const value = defaultValues[key] || element.props.value;
    const newElement = createElementFromCopy(element, undefined, elementActions(), rowIndex, undefined, { rowIndex }, { value });
    popupElements.push(newElement);
    popupChildren.push(getElementComponent(newElement, index, false));
  });

  setParserState(getParserState().elements.concat(popupElements));
  refreshShowLogic();

  const { elements, elementsIndex } = getParserState();
  let refreshedElements = [];
  let refreshedChildren = [];

  popupElements.forEach((el, index) => {
    const newEl = elements[elementsIndex[el.id]];
    refreshedElements.push(newEl);
    refreshedChildren.push(getElementComponent(el, index, false, undefined, { ...newEl.props }));
  });

  return { elements: refreshedElements, children: refreshedChildren };
}

function TableBuildRow(data, origRowIndex, rowIndex, tableHeadElements, elementActions, tableActions) {
  let rowElements = [];
  let rowValues = [];

  const { getTableProps, getUpdateModelFn, onUpdateState, getRefreshTableFn } = tableActions;
  const { controlTypeName } = getTableProps();
  const isReadOnlyTable = controlTypeName === 'SelectTable' || controlTypeName === 'HTCombo';
  const isSelectTable = controlTypeName === 'SelectTable';

  // If the table row added is from table type 'HTCombo' or 'SelectTable' then all the fields should be `Readonly` components

  data.forEach((col, colIndex) => {
    const columnEl = createElementFromCopy(
      { ...tableHeadElements[col.elementName], ...(isReadOnlyTable && { controlTypeName: 'ReadOnly' }) },
      col.value,
      {
        ...elementActions(),
        onChangeFn: () => {
          getUpdateModelFn(rowIndex, colIndex);
          getRefreshTableFn();
        },
        onUpdateState: onUpdateState ? onUpdateState(colIndex) : () => {},
      },
      rowIndex,
      undefined,
      {
        rowIndex,
        colIndex,
        origRowIndex: parseInt(origRowIndex, 10),
        entityId: col.entityId,
        ...(isSelectTable && { isRowSelected: col.isRowSelected }),
        ...(isReadOnlyTable && { origControlTypeName: tableHeadElements[col.elementName].controlTypeName }),
        ...(col.answerPageId && { answerPageId: col.answerPageId }),
        ...(col.sourceTableId && { sourceTableId: col.sourceTableId }),
      },
      {
        isHiddenMount: true,
        ...(isReadOnlyTable && { label: undefined, helpText: undefined }),
      },
    );
    rowElements.push(columnEl);
    rowValues.push(col.value);
  });

  return { rowElements, rowValues };
}

function TableBuildRows(data, tableHeadElements, elementActions, tableActions) {
  let rowsElements = [];
  let rowsValues = [];

  Object.entries(data).forEach(([key, elements], rowIndex) => {
    // derive our origRowIndex from either the key or if it's provided later when defined
    let curEl = elements[0];

    const origRowIndex = curEl && curEl.origRowIndex ? curEl.origRowIndex : key;
    const currentRow = TableBuildRow(elements, origRowIndex, rowIndex, tableHeadElements, elementActions, tableActions);

    rowsElements.push(...currentRow.rowElements);
    rowsValues.push(currentRow.rowValues);
  });

  return { rowsElements, rowsValues };
}

function TableDeleteRowInParser(elementsToRemove, elementActions) {
  const { getParserState, setParserState } = elementActions();
  const { elements: currentElements } = getParserState();

  const excludedElements = elementsToRemove.map(el => el.id);
  const cleanElements = currentElements.filter(el => !excludedElements.includes(el.id));
  setParserState(cleanElements);
}

function TableRefreshRow(elementsToRefresh, elementActions) {
  const { getParserState } = elementActions();
  const { elements, elementsIndex } = getParserState();

  const idsToRefresh = elementsToRefresh.map(el => el.id);
  idsToRefresh.forEach(id => {
    const position = elementsIndex[id];
    elements[position].updateComponent({ ...elements[position].props });
  });
}

function TableUpdateRowInParser(updatedElements, rowIndex, elementActions) {
  const { getParserState, refreshShowLogic, setFormValues } = elementActions();
  const { elements: currentElements, logicIndex } = getParserState();

  Object.entries(updatedElements).forEach(([key, val], index) => {
    const positions = logicIndex[key];

    positions.forEach(position => {
      let currentEl = currentElements[position];
      if (currentEl.rowIndex === rowIndex && val !== currentEl.props.value) {
        currentEl.props.value = val;
        currentEl.updateComponent({ ...currentEl.props });
      }
    });
  });

  refreshShowLogic();
  setFormValues();
}

export {
  TableBuildHead,
  TableAddRow,
  TableGetChildren,
  TableDeleteRows,
  TableWatchShowLogic,
  TableBuildForm,
  TableRefreshRow,
  TableBuildColumnOptions,
  TableDeleteRowInParser,
  TableBuildRows,
  TableUpdateRowInParser,
  refreshColumnsOptions,
};
